/**
 * 작성자 : 홍선영
 * 날짜 : 2024.05.20
 * 기능 : 근로자 출입 현황 대시보드 컴포넌트 (다중현장)
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { apiGet } from '../../services/_common';
import { PhotoTableListStyle } from '../../assets/styles/PhotoTableList';
import IssueGuide from '../IssueGuide';
import Slider from 'react-slick';
import { ERROR } from '../../_constants';
import SkeletonTable from '../SkeletonTable';

interface Iprops {
  userInfo: any;
  refetchInterval: number;
  uniqueKey: string;
}

interface IworkerAttend {
  aImg?: any;
  wCd: string;
  wName: string;
  sjCd: string;
  sjName: string;
  wPrejobtype: string;
  wPrejobtypeName: string;
  wJobtype: string;
  wJobtypeName: string;
  aDate: string;
  fLocationName: string;
  aTime: string;
}

export const WorkerAttendMultiSite = ({ userInfo, refetchInterval, uniqueKey }: Iprops) => {
  const { t } = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);

  // 근로자 출입현황 조회
  const { data, isLoading, isError } = useQuery([`attendMultiGet_${uniqueKey}`, userInfo.hCd, userInfo.sCd], () => fetchData(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && userInfo.sCd !== '00000',
    refetchInterval,
  });

  const fetchData = async () => {
    try {
      const req = { hCd: userInfo.hCd, sCd: userInfo.sCd };
      const res = await apiGet({ path: `/attend/multi`, req });
      return res.data.data;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const memoizedSettings = useMemo(
    () => ({
      dots: true,
      dotsClass: 'slick-dots slick-thumb',
      infinite: true,
      speed: 1000,
      slidesToShow: data?.attendList?.length || 1,
      slidesToScroll: 1,
      autoplay: data?.attendList?.length >= 2 || false,
      autoplaySpeed: 20 * 1000,
      beforeChange: (current: number, next: number) => setActiveSlide(next),
    }),
    [data]
  );

  const loading = !data || isLoading;

  if (isError) return <IssueGuide type='error' text={ERROR} />;

  return (
    <PhotoTableListStyle>
      <div className='widget-header safety'>
        <div className='widget-title flex-between'>
          <span>
            {t('근로자 출입현황')}
            <span className='badge'>{data?.totalWorkers ?? 0}</span>
          </span>
        </div>
      </div>
      {loading ? (
        <SkeletonTable rows={14} />
      ) : (
        <div className='widget-body photoTable'>
          <div className='innerTab nonClick'>
            <Slider key={activeSlide} {...memoizedSettings}>
              {data?.attendList?.map((el: any) => (
                <div key={el.sCd} className={activeSlide === el.sliderIndex ? 'active' : undefined}>
                  <span className='tabName'>{el.sName}</span> ({el.count})
                </div>
              ))}
            </Slider>
          </div>
          <div className='sliderWrapper'>
            <div className='text-container active'>
              <WorkerList key={activeSlide} workerList={data?.attendList.find((el: any) => el.sliderIndex === activeSlide)?.worker || []} />
            </div>
          </div>
        </div>
      )}
    </PhotoTableListStyle>
  );
};

const WorkerList = React.memo(({ workerList }: { workerList: IworkerAttend[] }) => {
  const animationStyle = useMemo(() => {
    return workerList.length > 14
      ? {
          animation: `slide ${workerList.length * 1.6}s linear infinite`,
          paddingTop: '2rem',
        }
      : undefined;
  }, [workerList.length]);

  if (workerList.length === 0) return null;
  return (
    <div style={animationStyle}>
      {workerList?.map((worker: IworkerAttend, index) => (
        <div key={`${worker.wCd}_${index}`} className='rowWrapper flex-basic'>
          <div className='flex-basic'>
            <div className='imgWrapper'>{worker.aImg ? <img src={worker.aImg} alt='worker img' /> : <span className='material-symbols-rounded'>person </span>}</div>
            <div className='flex-col'>
              <div className='flex-basic flex-between'>
                <div className='ellipsis' style={{ paddingRight: '0.5rem' }}>
                  {worker.wName}
                </div>
                <div className='buttonsWrapper flex-basic'>
                  <button type='button' className='amber'>
                    <span>{worker.fLocationName}</span>
                  </button>
                </div>
              </div>

              <div className='flex-basic flex-between'>
                <div style={{ paddingRight: '0.5rem' }}>
                  {worker.sjName} {worker.wJobtypeName ? `/ ${worker.wJobtypeName}` : ''}
                </div>
                <div className='buttonsWrapper flex-basic'>
                  <button type='button' className='gray'>
                    <span>{worker.aTime}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

WorkerList.displayName = 'WorkerList';
